<template>
	<div :class="bemm()">
		<span :class="bemm('pre')" v-if="pre">{{ pre }}</span>
		<Icon v-if="icon" :name="icon" :class="bemm('icon')" />
		<span :class="bemm('id-tooltip')">
			<TooltipWrapper
				:disabled="!isIdEllipsed || !id"
				:text="id"
				placement="top"
				:max-width="420"
				:multiline="true"
				stretch
			>
				<span
					ref="idRef"
					:class="[
						bemm('id'),
						{ [bemm('id', 'plain')]: $slots.default || displayValue },
					]"
				>
					<slot>{{ displayValue || id }}</slot>
				</span>
			</TooltipWrapper>
		</span>
		<TooltipWrapper
			v-model="showTooltip"
			:text="t('common.tooltips.copied')"
			trigger="manual"
			placement="top"
		>
			<Icon
				:name="copied ? Icons.CHECK : Icons.COPY"
				:class="[bemm('copy-icon'), { [bemm('copy-icon', 'copied')]: copied }]"
				@click.native.stop="handleCopyId"
				:title="t('common.tooltips.copyIdToClipboard')"
			/>
		</TooltipWrapper>
	</div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyToClipboard } from '../utils/clipboard';
import { useBemm } from 'bemm';
import { Icon, IconNameOrString, Icons } from '../Icon';
import { TooltipWrapper } from '../Tooltip';
import { useEllipsisDetection } from '../composables';

const props = defineProps({
	id: {
		type: String as PropType<string>,
		required: false,
		default: '',
	},
	pre: {
		type: String as PropType<string>,
		default: '',
	},
	displayValue: {
		type: String as PropType<string>,
		default: '',
	},
	icon: {
		type: String as PropType<IconNameOrString>,
		default: '',
	},
});

const emit = defineEmits(['copied']);
const { t } = useI18n();

const copied = ref(false);
const showTooltip = ref(false);
const { elementRef: idRef, isEllipsed: isIdEllipsed } = useEllipsisDetection([
	() => props.id,
	() => props.displayValue,
]);
let timeout: ReturnType<typeof setTimeout> | null = null;

const handleCopyId = () => {
	void copyToClipboard(props.id);
	emit('copied', props.id);

	copied.value = true;
	showTooltip.value = true;

	if (timeout) clearTimeout(timeout);
	timeout = setTimeout(() => {
		copied.value = false;
		showTooltip.value = false;
	}, 2000);
};

const bemm = useBemm('id-view', {
	return: 'string',
	includeBaseClass: true,
});

onBeforeUnmount(() => {
	if (timeout) {
		clearTimeout(timeout);
	}
});
</script>

<style lang="scss">
.id-view {
	--id-view-background: color-mix(
		in srgb,
		var(--color-secondary),
		var(--color-background) 95%
	);
	--id-view-border: color-mix(
		in srgb,
		var(--color-secondary),
		var(--color-background) 80%
	);

	--id-view-color: color-mix(
		in srgb,
		var(--color-secondary),
		var(--color-foreground) 50%
	);

	background-color: var(--id-view-background);
	border: 1px solid var(--id-view-border);
	color: var(--id-view-color);
	font-weight: var(--font-weight-bold);
	border-radius: var(--border-radius);
	width: fit-content;
	padding: var(--space-s);
	display: flex;
	gap: var(--space-s);
	align-items: center;
	min-width: 0;
	max-width: 100%;

	&__pre {
		flex: 0 0 auto;
		font-weight: var(--font-weight-bold);
		margin-right: 8px;
	}

	&__id-tooltip {
		flex: 1 1 auto;
		min-width: 0;
		max-width: 100%;
		overflow: hidden;
	}

	&__icon {
		color: var(--id-view-color);
		flex-shrink: 0;
	}

	&__id {
		display: block;
		flex: 1 1 auto;
		font-family: 'Courier New', Courier, monospace;
		font-size: var(--font-size-s);
		min-width: 0;
		max-width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		&--plain {
			font-family: inherit;
		}
	}

	&__copy-icon {
		flex: 0 0 auto;
		color: var(--color-secondary);
		cursor: pointer;
		transition: color 0.2s ease;

		&:hover {
			color: var(--color-foreground);
		}

		&--copied {
			color: var(--color-success);

			&:hover {
				color: var(--color-success);
			}
		}
	}
}
</style>
